import React, { useEffect, useState } from 'react';
import { ProductBestPriceNameplate } from 'common/components/product-price-view/product-best-price-nameplate';
import { cx } from 'linaria';
import { styles } from 'common/components/product-price-view/styles';
import {
  marginLeftSignaturePrimarySticky,
  marginLeftSignatureSecondarySticky,
  marginSlashPrimarySticky,
  marginSlashSecondarySticky,
  oldNewPriceStickyContainer,
  oldPriceStickyContainer,
  priceFractCurrencyStickyStyle,
  priceMainStickyStyle,
  priceOldNewStickyStyle,
  priceSecondaryStickyStyle,
  priceSpecialStickyStyle,
  secondaryPriceHidden,
  specialNewCurrencyPadding,
} from 'common/components/product-price-view/sticky.styles';
import { Typography } from 'fronton-react';
import { jcm } from 'utils';
import { SubtitleView } from 'common/components/product-price-view/subtitle-view';
import { ProductDiscountPercent } from 'common/components/product-price-view/product-discount-percent';
import {
  getFormattedOldPrice,
  getFormattedPrice,
  getNumericFract,
} from 'common/components/product-price-view/utils';
import { ProductPriceViewStickyProps } from './types';

export const ProductPriceViewSticky: React.FC<ProductPriceViewStickyProps> = ({
  hasThreePrices,
  currency,
  fract,
  intPart,
  prevPrice,
  slot,
  unit,
  variant = 'primary-price',
  isBestPrice,
  className,
  seoMeta,
  isStickyValue,
  discountPercent,
  ...htmlProps
}) => {
  const isSticky = !!isStickyValue;
  const [stickySpecialStyle, setStickySpecialStyle] = useState('');
  const isOldNewPrice = variant === 'old-new-price';

  useEffect(() => {
    if (isBestPrice) {
      setStickySpecialStyle(priceSpecialStickyStyle);
    } else {
      setStickySpecialStyle(cx(priceSpecialStickyStyle, priceOldNewStickyStyle));
    }
  }, [isBestPrice]);

  if (isOldNewPrice || isBestPrice) {
    return (
      <div data-qa="sticky-old-new-price" className={oldNewPriceStickyContainer}>
        {isBestPrice ? (
          <ProductBestPriceNameplate isSticky={isSticky} />
        ) : (
          <div className={styles.oldPriceWrapper}>
            <ProductDiscountPercent percent={discountPercent} />

            <div className={styles.oldPriceCrossed}>
              <Typography variant="s" size="body_accent" color="text-primary">
                {getFormattedOldPrice(prevPrice)}
              </Typography>
              <Typography
                variant="s"
                size="body_accent"
                color="text-primary"
                className={styles.oldCurrencyWrap}
              >
                {currency}
              </Typography>
            </div>
          </div>
        )}

        <div className={cx(styles.newPriceContainer, isBestPrice && styles.textPrimaryStyle)}>
          <span data-qa="sticky-price-special" className={cx(stickySpecialStyle)}>
            {getFormattedPrice(intPart) + (getNumericFract(fract) ? ',' : '')}
          </span>

          {getNumericFract(fract) > 0 && <span className={priceFractCurrencyStickyStyle}>{fract}</span>}
          <span
            data-qa="sticky-price-fract-currency"
            className={cx(priceFractCurrencyStickyStyle, specialNewCurrencyPadding)}
          >{`${currency}${unit ? `/${unit}` : ''}`}</span>
        </div>

        {isBestPrice && isOldNewPrice && (
          <div data-qa="sticky-old-price" className={cx(oldPriceStickyContainer, styles.oldPriceCrossed)}>
            <Typography
              variant="m"
              size="body_accent"
              color="text-primary"
              className={priceFractCurrencyStickyStyle}
            >
              {getFormattedOldPrice(prevPrice)}
            </Typography>
            <Typography
              variant="s"
              size="body_accent"
              className={cx(priceFractCurrencyStickyStyle, styles.oldCurrencyWrap)}
            >
              {currency}
            </Typography>
          </div>
        )}
      </div>
    );
  }
  return (
    <div
      data-qa="sticky-primary-price"
      className={jcm({
        [priceMainStickyStyle]: variant === 'primary-price',
        [secondaryPriceHidden]: !!hasThreePrices && variant === 'second-price',
        [priceSecondaryStickyStyle]: variant === 'second-price',
        [styles.subtitleView]: variant === 'subtitle',
      })}
      {...htmlProps}
    >
      {variant !== 'subtitle' && (
        <>
          {getFormattedPrice(intPart)}
          {getNumericFract(fract) > 0 && (
            <>
              <span className={styles.comma}>,</span>
              <span
                className={jcm({
                  [priceFractCurrencyStickyStyle]: variant === 'primary-price',
                  [secondaryPriceHidden]: !!hasThreePrices && variant === 'second-price',
                  [priceSecondaryStickyStyle]: variant === 'second-price',
                })}
              >
                {fract}
              </span>
            </>
          )}
          <span
            data-qa="sticky-second-price"
            className={jcm({
              [priceFractCurrencyStickyStyle]: variant === 'primary-price',
              [secondaryPriceHidden]: !!hasThreePrices && variant === 'second-price',
              [priceSecondaryStickyStyle]: variant === 'second-price',
              [marginLeftSignaturePrimarySticky]: variant === 'primary-price',
              [marginLeftSignatureSecondarySticky]: variant === 'second-price',
            })}
          >
            {currency}
            {unit && (
              <>
                <span
                  className={jcm({
                    [priceFractCurrencyStickyStyle]: variant === 'primary-price',
                    [secondaryPriceHidden]: !!hasThreePrices && variant === 'second-price',
                    [priceSecondaryStickyStyle]: variant === 'second-price',
                    [marginSlashPrimarySticky]: variant === 'primary-price',
                    [marginSlashSecondarySticky]: variant === 'second-price',
                  })}
                >
                  /
                </span>
                {unit}
              </>
            )}
          </span>
        </>
      )}
      {variant === 'subtitle' && (
        <SubtitleView
          fract={fract}
          currency={currency}
          unit={unit}
          intPart={intPart}
          prevPrice={prevPrice}
        />
      )}
      {seoMeta}
    </div>
  );
};
